import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalBody,
  Input,
  Label,
  FormFeedback,
  Button,
} from "reactstrap";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import { getUserRole, fetchRoles } from "./api/usersApi";
import getAuthUserStorage from "../../helpers/getAuthUserStorage";
import "./UserModal.css";

interface UserModalProps {
  show: boolean;
  onClose: () => void;
  onSubmit: (values: UserFormValues) => void;
  isLoading?: boolean;
  error?: string | null;
  editMode?: boolean;
  initialData?: UserFormValues | null;
  showSuccess?: boolean;
  successType?: "created" | "resent" | "createError" | "resendError";
  successErrorMessage?: string | null;
  onSuccessClose?: () => void;
  onRetry?: () => void;
}

export interface UserFormValues {
  firstName: string;
  lastName: string;
  email: string;
  role: string;
}

interface Role {
  id: string;
  name: string;
  description: string;
}

const UserModal: React.FC<UserModalProps> = ({
  show,
  onClose,
  onSubmit,
  isLoading = false,
  error = null,
  editMode = false,
  initialData = null,
  showSuccess = false,
  successType = "created",
  successErrorMessage = null,
  onSuccessClose,
  onRetry,
}) => {
  const { t } = useTranslation();
  // Get current user role to determine available role options
  const currentUserRole = getUserRole();
  const isSuperAdminOrAdmin =
    currentUserRole === "SUPER_ADMIN" || currentUserRole === "ADMIN";
  const isTenantAdmin = currentUserRole === "TENANT_ADMIN";

  // State for roles fetched from API
  const [roles, setRoles] = useState<Role[]>([]);
  const [rolesLoading, setRolesLoading] = useState(false);

  // Fetch roles when modal opens
  useEffect(() => {
    if (show && !editMode) {
      const loadRoles = async () => {
        try {
          setRolesLoading(true);
          const response = await fetchRoles();
          setRoles(response.data || []);
        } catch (error) {
          console.error("Error fetching roles:", error);
        } finally {
          setRolesLoading(false);
        }
      };
      loadRoles();
    }

    // Reset form when opening in create mode without initial data
    if (show && !editMode && !initialData) {
      validation.resetForm();
    }
  }, [show, editMode, initialData]);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      firstName: initialData?.firstName || "",
      lastName: initialData?.lastName || "",
      email: initialData?.email || "",
      role: initialData?.role || "",
    },
    validationSchema: Yup.object({
      firstName: Yup.string().required(
        t("users.modal.validation.firstNameRequired")
      ),
      lastName: Yup.string().required(
        t("users.modal.validation.lastNameRequired")
      ),
      email: Yup.string()
        .email(t("users.modal.validation.invalidEmail"))
        .required(t("users.modal.validation.emailRequired")),
      role: Yup.string().required(t("users.modal.validation.roleRequired")),
    }),
    onSubmit: (values) => {
      // Get tenantId from auth storage
      const authUser = getAuthUserStorage();
      const tenantId = authUser?.tenant?.id;

      // Transform the data to match backend expectations
      if (editMode) {
        // Edit mode: just send basic fields with role name
        const userData = {
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          role: values.role,
        };
        onSubmit(userData as any);
      } else {
        // Create mode: find roleId from fetched roles and send with tenantId
        const selectedRole = roles.find((r) => r.name === values.role);
        const userData = {
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          roleId: selectedRole?.id, // Use roleId from API
          tenantId: tenantId, // Always send tenantId for user-tenant relationship
        };
        onSubmit(userData as any);
      }
    },
  });

  const handleClose = () => {
    validation.resetForm();
    onClose();
  };

  const handleSuccessClose = () => {
    if (onSuccessClose) {
      onSuccessClose();
    }
  };

  const handleRetry = () => {
    // Call the parent's retry handler which will:
    // 1. Close this error modal
    // 2. Reopen the create modal with the saved form data
    if (onRetry) {
      onRetry();
    }
  };

  // Success Modal
  if (showSuccess) {
    const isError =
      successType === "createError" || successType === "resendError";

    const modalContent = {
      created: {
        title: t("users.modal.result.created.title"),
        message: t("users.modal.result.created.message"),
      },
      resent: {
        title: t("users.modal.result.resent.title"),
        message: t("users.modal.result.resent.message"),
      },
      createError: {
        title: t("users.modal.result.createError.title"),
        message: t("users.modal.result.createError.message"),
      },
      resendError: {
        title: t("users.modal.result.resendError.title"),
        message: t("users.modal.result.resendError.message"),
      },
    };

    const content = modalContent[successType];

    // Use custom error message if provided, otherwise use default
    const displayMessage =
      isError && successErrorMessage ? successErrorMessage : content.message;

    return (
      <Modal
        isOpen={show}
        toggle={handleSuccessClose}
        centered
        className="success-modal"
      >
        <ModalBody className="success-modal-body">
          <div className="success-container">
            {/* Success/Error Icon */}
            <div className={`success-icon-wrapper ${isError ? "error" : ""}`}>
              <i
                className={`${
                  isError ? "ri-close-circle-fill" : "ri-checkbox-circle-fill"
                } success-icon ${isError ? "error" : ""}`}
              />
            </div>

            {/* Success/Error Header */}
            <div className="success-header">
              <h3 className="create-user-title">{content.title}</h3>
              <p className="create-user-subtitle">{displayMessage}</p>
            </div>

            {/* Action Buttons */}
            <div className="success-button-wrapper">
              {isError ? (
                // Error state: Show Retry and Cancel buttons
                <>
                  <Button
                    type="button"
                    color="primary"
                    className="btn-send-invitation"
                    onClick={handleRetry}
                    style={{ marginTop: "8px" }}
                  >
                    {t("common.actions.retry")}
                  </Button>
                  <Button
                    type="button"
                    className="btn-cancel"
                    onClick={handleSuccessClose}
                    style={{ marginTop: "8px" }}
                  >
                    {t("common.actions.cancel")}
                  </Button>
                </>
              ) : (
                // Success state: Show Done button
                <Button
                  type="button"
                  color="primary"
                  className="btn-send-invitation"
                  onClick={handleSuccessClose}
                >
                  {t("users.modal.done")}
                </Button>
              )}
            </div>
          </div>
        </ModalBody>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={show}
      toggle={handleClose}
      centered
      className="create-user-modal"
    >
      <ModalBody className="create-user-modal-body">
        <div className="create-user-container">
          {/* Header */}
          <div className="create-user-header">
            <h3 className="create-user-title">
              {editMode
                ? t("users.modal.editTitle")
                : t("users.modal.createTitle")}
            </h3>
            <p className="create-user-subtitle">
              {editMode
                ? t("users.modal.editSubtitle")
                : t("users.modal.createSubtitle")}
            </p>
          </div>

          {/* Error Alert */}
          {error && (
            <div className="error-alert">
              <i className="ri-error-warning-line"></i>
              <span>{error}</span>
            </div>
          )}

          {/* Form */}
          <form onSubmit={validation.handleSubmit} className="create-user-form">
            {/* First Name Field */}
            <div className="form-field">
              <Label className="form-label">
                {t("common.labels.firstName")}
                <span className="required-asterisk">*</span>
              </Label>
              <Input
                type="text"
                name="firstName"
                className="form-input"
                placeholder=" "
                value={validation.values.firstName}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                invalid={
                  validation.touched.firstName && validation.errors.firstName
                    ? true
                    : false
                }
              />
              {validation.touched.firstName && validation.errors.firstName && (
                <FormFeedback type="invalid">
                  {validation.errors.firstName}
                </FormFeedback>
              )}
            </div>

            {/* Last Name Field */}
            <div className="form-field">
              <Label className="form-label">
                {t("common.labels.lastName")}
                <span className="required-asterisk">*</span>
              </Label>
              <Input
                type="text"
                name="lastName"
                className="form-input"
                placeholder=" "
                value={validation.values.lastName}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                invalid={
                  validation.touched.lastName && validation.errors.lastName
                    ? true
                    : false
                }
              />
              {validation.touched.lastName && validation.errors.lastName && (
                <FormFeedback type="invalid">
                  {validation.errors.lastName}
                </FormFeedback>
              )}
            </div>

            {/* Email Field */}
            <div className="form-field">
              <Label className="form-label">
                {t("common.labels.email")}
                <span className="required-asterisk">*</span>
              </Label>
              <Input
                type="email"
                name="email"
                className="form-input"
                placeholder=" "
                value={validation.values.email}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                invalid={
                  validation.touched.email && validation.errors.email
                    ? true
                    : false
                }
              />
              {validation.touched.email && validation.errors.email && (
                <FormFeedback type="invalid">
                  {validation.errors.email}
                </FormFeedback>
              )}
            </div>

            {/* Role Field */}
            <div className="form-field">
              <Label className="form-label">
                {t("common.labels.role")}
                <span className="required-asterisk">*</span>
              </Label>
              <Input
                type="select"
                name="role"
                className="form-select"
                value={validation.values.role}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                invalid={
                  validation.touched.role && validation.errors.role
                    ? true
                    : false
                }
              >
                <option value="">{t("users.modal.selectRole")}</option>
                {isSuperAdminOrAdmin ? (
                  // Super Admin and Admin can only create Super Admin and Admin
                  <>
                    <option value="SUPER_ADMIN">
                      {t("common.roles.SUPER_ADMIN")}
                    </option>
                    <option value="ADMIN">{t("common.roles.ADMIN")}</option>
                  </>
                ) : isTenantAdmin ? (
                  // Tenant Admin can only create tenant admin and tenant user roles
                  <>
                    <option value="TENANT_ADMIN">
                      {t("common.roles.TENANT_ADMIN")}
                    </option>
                    <option value="TENANT_USER">
                      {t("common.roles.TENANT_USER")}
                    </option>
                  </>
                ) : null}
              </Input>
              {validation.touched.role && validation.errors.role && (
                <FormFeedback type="invalid">
                  {validation.errors.role}
                </FormFeedback>
              )}
            </div>

            {/* Action Buttons */}
            <div className="form-actions">
              <Button
                type="submit"
                color="primary"
                className="btn-send-invitation"
                disabled={isLoading}
              >
                {isLoading
                  ? editMode
                    ? t("users.modal.updating")
                    : t("users.modal.sendingInvitation")
                  : editMode
                  ? t("users.modal.updateUser")
                  : t("users.modal.sendInvitation")}
              </Button>
              <Button
                type="button"
                className="btn-cancel"
                onClick={handleClose}
                disabled={isLoading}
              >
                {t("common.actions.cancel")}
              </Button>
            </div>
          </form>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default UserModal;
